/**
 * Section icon registry — single source of truth for lesson-section icons.
 *
 * The AI writes a free-form `icon` hint onto every framework section, and it
 * uses a much wider vocabulary than the old 11-entry maps understood (a survey
 * of live drafts found 31 distinct names — flask, atom, telescope, compass,
 * sigma, alert-triangle, …). Anything unrecognised fell back to a generic
 * document icon, which is why nearly every section rendered the same glyph.
 *
 * Resolution order:
 *   1. the icon name, normalised (case/space/dash/underscore insensitive, so
 *      `check-circle`, `check_circle` and `checkCircle` all resolve)
 *   2. keywords in the section title — covers icon names we've never seen
 *   3. FileText
 */
import {
  Target, Sparkles, BookOpen, Image, Brain, Globe, CheckCircle2, RefreshCw,
  Star, FileText, FlaskConical, Atom, Telescope, Calculator, PencilLine,
  AlertTriangle, Compass, Bookmark, Network, Layers, Sigma, Battery, Zap,
  StickyNote, CircuitBoard, ListChecks, LineChart, Shapes, Microscope,
  Lightbulb, GraduationCap, Link2, Repeat,
} from 'lucide-react';

// Normalise so naming style never decides whether an icon resolves.
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Every key is pre-normalised. Synonyms intentionally collapse onto one icon.
const ICONS = {
  // objectives / overview
  target: Target, goal: Target, objective: Target, objectives: Target,
  compass: Compass, overview: Compass, intro: Compass, introduction: Compass,
  // ideas / concepts
  lightbulb: Lightbulb, idea: Lightbulb, bulb: Lightbulb,
  brain: Brain, concept: Brain, concepts: Brain, keyconcepts: Brain,
  nodes: Network, network: Network, mindmap: Network, graph: Network,
  layers: Layers, shapes: Shapes, structure: Layers,
  atom: Atom, molecule: Atom, physics: Atom, chemistry: Atom,
  // reading / lesson body
  book: BookOpen, bookopen: BookOpen, lesson: BookOpen, content: BookOpen,
  reading: BookOpen, notes: StickyNote, note: StickyNote,
  bookmark: Bookmark, recap: Bookmark, summary: Bookmark, revision: Bookmark,
  // science / lab
  flask: FlaskConical, beaker: FlaskConical, lab: FlaskConical,
  experiment: FlaskConical, experiments: FlaskConical,
  microscope: Microscope, telescope: Telescope, observation: Telescope,
  hypothesis: Telescope,
  circuit: CircuitBoard, circuitboard: CircuitBoard, battery: Battery,
  electronics: CircuitBoard,
  // maths
  sigma: Sigma, formula: Sigma, formulas: Sigma, equation: Sigma,
  equations: Sigma, function: Sigma, functionsquare: Sigma, squarefunction: Sigma,
  calculator: Calculator, calculation: Calculator, workedexamples: Calculator,
  // practice
  pencil: PencilLine, pencilline: PencilLine, edit: PencilLine,
  practice: PencilLine, exercise: PencilLine, exercises: PencilLine,
  problems: PencilLine, homework: PencilLine,
  cards: Layers, flashcards: Layers, flashcard: Layers, deck: Layers,
  list: ListChecks, listchecks: ListChecks, checklist: ListChecks,
  steps: ListChecks,
  // visual
  image: Image, picture: Image, visual: Image, visuals: Image,
  diagram: Shapes, diagrams: Shapes, model: Shapes, models: Shapes,
  chart: LineChart, linechart: LineChart, graphchart: LineChart,
  data: LineChart, analysis: LineChart,
  // warnings
  alert: AlertTriangle, alerttriangle: AlertTriangle, warning: AlertTriangle,
  mistake: AlertTriangle, mistakes: AlertTriangle, pitfall: AlertTriangle,
  pitfalls: AlertTriangle, misconception: AlertTriangle,
  misconceptions: AlertTriangle, caution: AlertTriangle,
  // world / application
  globe: Globe, world: Globe, realworld: Globe, application: Globe,
  applications: Globe, career: GraduationCap, careers: GraduationCap,
  // status / misc
  check: CheckCircle2, checkcircle: CheckCircle2, done: CheckCircle2,
  complete: CheckCircle2, quiz: CheckCircle2, assessment: CheckCircle2,
  refresh: RefreshCw, repeat: Repeat, review: RefreshCw,
  sparkle: Star, sparkles: Sparkles, star: Star, highlight: Star,
  flash: Zap, zap: Zap, quick: Zap, tip: Zap, tips: Zap,
  link: Link2, reference: Link2, references: Link2, resources: Link2,
  file: FileText, text: FileText, document: FileText,
};

// Title keywords, checked longest-first so "real world" beats "world".
const TITLE_HINTS = [
  ['misconception', AlertTriangle], ['mistake', AlertTriangle],
  ['pitfall', AlertTriangle], ['caution', AlertTriangle], ['warning', AlertTriangle],
  ['real-world', Globe], ['real world', Globe], ['application', Globe],
  ['career', GraduationCap],
  ['experiment', FlaskConical], ['lab', FlaskConical], ['practical', FlaskConical],
  ['hypothesis', Telescope], ['observation', Telescope],
  ['formula', Sigma], ['equation', Sigma], ['law', Sigma],
  ['worked example', Calculator], ['calculation', Calculator],
  ['practice', PencilLine], ['problem', PencilLine], ['exercise', PencilLine],
  ['homework', PencilLine], ['assignment', PencilLine],
  ['diagram', Shapes], ['model', Shapes], ['visual', Image], ['image', Image],
  ['chart', LineChart], ['data', LineChart], ['analysis', LineChart],
  ['recap', Bookmark], ['revision', Bookmark], ['summary', Bookmark],
  ['objective', Target], ['goal', Target],
  ['overview', Compass], ['introduction', Compass], ['intro', Compass],
  ['key concept', Brain], ['concept', Brain], ['theory', Brain],
  ['quiz', CheckCircle2], ['assessment', CheckCircle2], ['test', CheckCircle2],
  ['reference', Link2], ['resource', Link2],
  ['circuit', CircuitBoard], ['reaction', Atom],
  ['flashcard', Layers], ['card', Layers],
  ['step', ListChecks], ['checklist', ListChecks],
  ['tip', Zap], ['quick', Zap],
  ['lesson', BookOpen], ['content', BookOpen], ['reading', BookOpen],
  ['note', StickyNote],
];

export function getSectionIcon(iconName, title = '') {
  const key = norm(iconName);
  if (key && ICONS[key]) return ICONS[key];

  const t = String(title || '').toLowerCase();
  if (t) {
    for (const [needle, Icon] of TITLE_HINTS) {
      if (t.includes(needle)) return Icon;
    }
  }
  return FileText;
}

export default getSectionIcon;
